import React, { useEffect, useRef, useState } from 'react'
import { Flex, Text } from '@sendoutcards/quantum-design-ui'

import { BentoCard, BentoGridArea } from './BentoCard'

import { ContentPop } from 'src/home_page/components/ContentPop/ContentPop'
import { ProductPop } from 'src/home_page/components/ProductPop/ProductPop'
import { ImageDiffSlider } from 'src/design_system/components/ImageDiffSlider/ImageDiffSlider'
import { BlobSettingType } from 'src/design_system/components/GlassBackgroundSvgContainer/GlassBackgroundSvgContainer'
import { ExpandableItem } from '../ExpandableItem/ExpandableItem'
import { BentoCardFragment } from 'src/strapi/queries'

type BentoGridTexts = {
  title: string
  description?: string
  gridArea: BentoGridArea
}

type BentoGridContainerProps = {
  gridTexts?: BentoGridTexts
  isMobile?: boolean
  isFlexOverride?: boolean
  blobs?: BlobSettingType
  activeBentoCallback?: (id: number) => void
  isComponentRight?: boolean
  childrenGridArea?: BentoGridArea
  maxHeight?: string
  gridRowOverride?: string
  gridColumnOverride?: string
  textHeightOverride?: string
  textWidthOverride?: string
  cards: BentoCardFragment[]
}

// tslint:disable-next-line: cyclomatic-complexity score 25
export const BentoGridContainer: React.FC<BentoGridContainerProps> = ({
  cards,
  gridTexts,
  blobs,
  activeBentoCallback,
  children,
  isComponentRight = false,
  childrenGridArea,
  maxHeight = '620px',
  gridRowOverride,
  gridColumnOverride,
  isFlexOverride,
  textHeightOverride,
  textWidthOverride,
}) => {
  const [activeCardId, setActiveCardId] = useState<string | null>(null)
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null)

  const handleCardClick = (id: string, index: number) => {
    setActiveCardId(id)
    if (!!activeBentoCallback) {
      activeBentoCallback(index)
    }
  }

  const bentoContainerRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isSmallMobile, setIsSmallMobile] = useState(false)

  const areaToStyle = (a?: BentoGridArea) =>
    a
      ? {
          gridColumnStart: a.colStart,
          gridColumnEnd: a.colEnd,
          gridRowStart: a.rowStart,
          gridRowEnd: a.rowEnd,
        }
      : {}

  useEffect(() => {
    const bentoContainer = bentoContainerRef.current
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setIsMobile(entry.contentRect.width <= 860)
        setIsSmallMobile(entry.contentRect.width <= 500)
      }
    })

    if (bentoContainer) {
      resizeObserver.observe(bentoContainer)
    }

    return () => {
      if (bentoContainer) {
        resizeObserver.unobserve(bentoContainer)
      }
    }
  }, [])

  useEffect(() => {
    const firstReady = cards.find(c => !!c.gridArea)
    setActiveCardId(firstReady?.id ?? null)
  }, [cards])

  return (
    <div
      css={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <div
        css={{
          display: isFlexOverride ? 'flex' : 'grid',
          flexDirection: isFlexOverride ? 'column' : undefined,
          maxHeight: maxHeight,
          gridTemplateColumns: gridColumnOverride ?? 'repeat(20, 1fr)',
          gridTemplateRows: gridRowOverride ?? 'repeat(20, 1fr)',
          gap: '32px',
          height: '100%',
          width: '100%',
          maxWidth: '1300px',
          justifyItems: 'space-between',
        }}
        ref={bentoContainerRef}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            ...areaToStyle(childrenGridArea),
          }}
        >
          {children}
        </div>
        {gridTexts && (
          <div
            css={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              maxWidth: textWidthOverride ?? '1000px',
              height: 'inherit',
              minHeight: textHeightOverride ?? '90px',
              ...areaToStyle(gridTexts.gridArea),
            }}
          >
            <Text
              type="largeBody"
              content={gridTexts.title}
              style={{
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: 700,
                color: '#404040',
              }}
              outset={{ bottom: '8px' }}
            />
            {gridTexts.description && (
              <Text
                type="body"
                content={gridTexts.description}
                style={{
                  fontSize: isMobile ? '12px ' : '16px',
                  fontWeight: 400,
                  color: '#404040',
                }}
              />
            )}
          </div>
        )}

        {cards.map((card, index) => {
          const imgDiffObj = card?.detailsContent?.imageDiff
          if (!card.gridArea) return null // ⬅️ per-card gate, no fragments
          const { colStart, colEnd, rowStart, rowEnd } = card.gridArea

          return (
            <div
              key={card.id}
              css={{
                height: 'auto',
                minWidth: '220px',
                width: '100%',
                gridColumnStart: colStart,
                gridColumnEnd: colEnd,
                gridRowStart: rowStart,
                gridRowEnd: rowEnd,
              }}
            >
              <ExpandableItem
                closedState={
                  <BentoCard
                    blobs={blobs}
                    activeGradient="linear-gradient(126deg, #FF00F5, #25C1F3, #6B7280"
                    isMobile={isMobile}
                    isSmallMobile={isSmallMobile}
                    height="fit-content"
                    width={'100%'}
                    bento={card}
                    onSelect={() => handleCardClick(card.id, index)}
                    onLearnMore={
                      card.detailsContent || card.detailsProduct
                        ? () => setExpandedCardId(card.id)
                        : undefined
                    }
                    isActive={card.id === activeCardId}
                  />
                }
                isExpanded={card.id === expandedCardId}
                handleOnClose={() => setExpandedCardId(null)}
                openState={
                  card.detailsContent || card.detailsProduct ? (
                    <Flex
                      width="100%"
                      height="100%"
                      maxWidth={
                        isSmallMobile && expandedCardId === card.id
                          ? '95vw'
                          : undefined
                      }
                      backgroundColor="background"
                      borderRadius="medium"
                    >
                      {card.type === 'content' && card.detailsContent ? (
                        <ContentPop
                          shouldStackColumns={false}
                          isMobile={isMobile}
                          isSmallMobile={isSmallMobile}
                          contentPop={card.detailsContent}
                        >
                          {card.detailsContent.imageDiff && (
                            <Flex width="100%">
                              <ImageDiffSlider
                                leftImage={{
                                  image: imgDiffObj?.leftImage.url ?? '',
                                  alt: imgDiffObj?.leftAlt ?? '',
                                }}
                                rightImage={{
                                  image: imgDiffObj?.rightImage.url ?? '',
                                  alt: imgDiffObj?.rightAlt ?? '',
                                }}
                              />
                            </Flex>
                          )}
                        </ContentPop>
                      ) : card.type === 'product' && card.detailsProduct ? (
                        <ProductPop
                          product={card.detailsProduct}
                          action={{
                            title: 'Click me',
                            onClick: () => console.log('clicked'),
                          }}
                        />
                      ) : (
                        <></>
                      )}
                    </Flex>
                  ) : (
                    <></>
                  )
                }
                modalOptions={{
                  maxWidth:
                    expandedCardId !== card.id
                      ? 'initial'
                      : card.type === 'content'
                      ? '900px'
                      : '700px',
                  maxHeight: card.type === 'content' ? '90vh' : '70vh',
                  margin: '0 auto',
                  width:
                    isMobile && expandedCardId === card.id ? '95vw' : undefined,
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
